import { useQuery } from "@apollo/client";
import { Button, Sheet, Typography, Box } from "@mui/joy";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { GET_JOB_INFO } from "./auth/AuthLayout";
import { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useFeedback } from "./feedback/Service";
import useTracking from './useTracking';
import logo from '../assets/images/logo.svg';
import useUser from "./auth/useUser";

const SalesLanding = () => {
  const { clientId, jobId } = useParams();
  const [searchParams] = useSearchParams();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const isScreenSmall = useMediaQuery('(max-width:600px)');
  const { trackLandingVisited, trackLandingApplyNow } = useTracking();
  const { data: jobInfo } = useQuery(GET_JOB_INFO, {
    variables: { clientId, jobId },
    skip: !clientId || !jobId,
  });
  const { user } = useUser();

  const isDemo = user?.client?._id === clientId;

  const handleGetStarted = async e => {
    if (!isDemo) {
      window.gtag('event', "job_landing_get_started", {});
      trackLandingApplyNow(clientId, jobId, jobInfo?.getJobInfo?.title);
    }
    if (!isScreenSmall)
      return;
    e.preventDefault();
    const confirm = await feedback.confirm({
      title: "Mobile Warning",
      text: "The application process is optimized for desktop. Are you sure you want to continue on mobile?",
      confirmButtonText: "Continue"
    });
    if (confirm) {
      navigate(`/company/${clientId}/job/${jobId}/sign-up`);
    }
  }

  useEffect(() => {
    if (isDemo)
      return;
    window.gtag('event', "job_landing_visit", {});
    // Track landing page visit with UTM parameters
    trackLandingVisited(
      clientId,
      jobId,
      document.referrer || undefined,
      searchParams.get('utm_source') || undefined,
      searchParams.get('utm_medium') || undefined,
      searchParams.get('utm_campaign') || undefined
    );
  }, [clientId, jobId, searchParams, trackLandingVisited, isDemo]);

  if (!jobInfo)
    return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        pt: 4,
      }}
    >
      <Sheet
        sx={{
          py: 8,
          px: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: "100%",
          maxWidth: 'sm',
          mx: 'auto',
        }}
      >
        {/* Simple Logo */}
        <Box sx={{ mb: 6 }}>
          <img
            src={logo}
            alt="Cavalry HR"
            style={{ height: '32px', opacity: 1 }}
          />
        </Box>

        {/* Main Content */}
        <Typography
          level="h1"
          sx={{
            mb: 6,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            fontWeight: 600,
            lineHeight: 1.3,
            // color: 'neutral.800'
          }}
        >
          You&apos;re applying for the{' '}
          <Box component="span" sx={{ color: 'primary.500' }}>
            {jobInfo.getJobInfo.title}
          </Box>{' '}
          {!jobInfo.getJobInfo.hideClientName &&
            <>
              role at <Box component="span" sx={{ color: 'primary.500' }}>{jobInfo.getJobInfo.client.company}</Box>
            </>
          }
        </Typography>

        {/* Simple CTA */}
        <Button
          variant="solid"
          color="primary"
          size="lg"
          component={Link}
          to={`/company/${clientId}/job/${jobId}/sign-up`}
          onClick={handleGetStarted}
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: 'md',
          }}
        >
          Apply Now
        </Button>
      </Sheet>
    </Box>
  );
}

export default SalesLanding;